import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Label,
  Table,
  Badge,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { toast } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MercantilScrapingModal from "../../../Components/Common/MercantilScrapingModal";
import HistorialCorridas from "./HistorialCorridas";
import {
  getSolicitudesMercantilNuevas,
  mercantilBuscarYRegistrar,
} from "../../../helpers/backend_helper";

const TABS = {
  scraping: "scraping",
  individual: "individual",
  csv: "csv",
  historial: "historial",
};

// Formatear fechas
// Componente de loader profesional (SVG animado)
const LoaderButton = () => (
  <svg
    className="me-2"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <style>{`
      .spinner_sIW1 { animation: spinner_ZPKt 1.2s cubic-bezier(0.52,0.6,0.25,0.99) infinite }
      .spinner_98HH { animation-delay: 0.2s }
      .spinner_bPQA { animation-delay: 0.4s }
      @keyframes spinner_ZPKt {
        0% { transform: translateY(0) scale(1); opacity: 1 }
        50% { transform: translateY(8px) scale(0.6); opacity: 0.4 }
        100% { transform: translateY(0) scale(1); opacity: 1 }
      }
    `}</style>
    <circle className="spinner_sIW1" cx="4" cy="12" r="3" fill="currentColor" />
    <circle className="spinner_sIW1 spinner_98HH" cx="12" cy="12" r="3" fill="currentColor" />
    <circle className="spinner_sIW1 spinner_bPQA" cx="20" cy="12" r="3" fill="currentColor" />
  </svg>
);

// Mapear errores HTTP a mensajes amigables para el usuario
const mensajeErrorAmigable = (error) => {
  const status = error?.status || error?.response?.status;
  const detail = error?.response?.data?.detail || error?.detail;

  if (detail && typeof detail === "string") {
    // Si el backend envía un mensaje detallado, usarlo directamente
    return detail;
  }

  switch (status) {
    case 400:
      return "La solicitud no es válida. Verifique el identificador ingresado.";
    case 403:
      return "No se encontró la orden en el portal de Seguros Mercantil. Verifique que el identificador sea correcto.";
    case 404:
      return "La orden no existe o no está disponible en Mercantil.";
    case 409:
      return "La orden ya existe en el sistema.";
    case 422:
      return "Los datos de la orden no cumplen con los requisitos.";
    case 500:
      return "Ocurrió un error interno en el servidor. Intente nuevamente más tarde.";
    case 502:
    case 503:
      return "El portal de Mercantil no está disponible en este momento. Intente más tarde.";
    case 504:
      return "La consulta a Mercantil tardó demasiado. Intente nuevamente.";
    default:
      return "No se pudo completar la operación. Por favor intente de nuevo.";
  }
};

const formatearFecha = (fecha) => {
  if (!fecha) return "";
  return fecha instanceof Date
    ? fecha.toISOString().split("T")[0]
    : new Date(fecha).toISOString().split("T")[0];
};

// Obtener fecha por defecto (hoy)
const obtenerFechaDefault = () => {
  return formatearFecha(new Date());
};

// Parser CSV con separador punto y coma
const parsearCSV = (contenido) => {
  const lineas = contenido.trim().split("\n");
  if (lineas.length < 2) {
    throw new Error("El archivo CSV está vacío o no tiene datos");
  }

  // Primera línea son los encabezados
  const encabezados = lineas[0].split(";").map(h => h.trim());

  // Buscar índice de la columna # de orden (1)
  const indiceOrden = encabezados.findIndex(h =>
    h.toLowerCase().includes("# de orden") ||
    h.toLowerCase().includes("orden")
  );

  // Buscar índice de la columna Fecha de creación
  const indiceFecha = encabezados.findIndex(h =>
    h.toLowerCase().includes("fecha de creación") ||
    h.toLowerCase().includes("fecha") && h.toLowerCase().includes("creación")
  );

  if (indiceOrden === -1) {
    throw new Error("No se encontró la columna '# de orden (1)' en el CSV");
  }

  // Extraer datos de las filas
  const datos = [];
  for (let i = 1; i < lineas.length; i++) {
    const campos = lineas[i].split(";");
    if (campos.length > indiceOrden) {
      const identificador = campos[indiceOrden].trim().toUpperCase();
      if (identificador) {
        const fechaCreacion = indiceFecha !== -1 && campos.length > indiceFecha
          ? campos[indiceFecha].trim()
          : "N/A";
        datos.push({
          identificador,
          fechaCreacion
        });
      }
    }
  }

  return datos;
};

const ValidarMercantil = () => {
  // Estados
  const [archivoCSV, setArchivoCSV] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState("");
  const [identificadoresCSV, setIdentificadoresCSV] = useState([]);
  const [solicitudesSistema, setSolicitudesSistema] = useState([]);
  const [modoFecha, setModoFecha] = useState("single"); // "single" o "range"
  const [fechaSeleccionada, setFechaSeleccionada] = useState(obtenerFechaDefault());
  const [fechaInicio, setFechaInicio] = useState(obtenerFechaDefault());
  const [fechaFin, setFechaFin] = useState(obtenerFechaDefault());
  const [cargando, setCargando] = useState(false);
  const [resultados, setResultados] = useState(null);

  // Estados para búsqueda individual por identificador
  const [identificadorBusqueda, setIdentificadorBusqueda] = useState("");
  const [buscandoIndividual, setBuscandoIndividual] = useState(false);
  const [resultadoIndividual, setResultadoIndividual] = useState(null);

  // Estados para scraping masivo (modal)
  const [showScrapingModal, setShowScrapingModal] = useState(false);

  // Tab activa persistida en hash (#scraping, #individual, #csv, #historial)
  const readHashTab = () => {
    const h = (window.location.hash || "").replace("#", "");
    return TABS[h] || TABS.scraping;
  };
  const [activeTab, setActiveTab] = useState(readHashTab);

  useEffect(() => {
    const onHash = () => setActiveTab(readHashTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const cambiarTab = (t) => {
    setActiveTab(t);
    if (window.location.hash !== `#${t}`) {
      window.history.replaceState(null, "", `#${t}`);
    }
  };

  // Manejar selección de archivo
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Por favor seleccione un archivo CSV");
      return;
    }

    setNombreArchivo(file.name);
    setArchivoCSV(file);
    setResultados(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const contenido = event.target.result;
        const datos = parsearCSV(contenido);
        setIdentificadoresCSV(datos);
        toast.success(`CSV cargado: ${datos.length} órdenes encontradas`);
      } catch (error) {
        toast.error(`Error al leer CSV: ${error.message}`);
        setIdentificadoresCSV([]);
      }
    };
    reader.onerror = () => {
      toast.error("Error al leer el archivo");
      setIdentificadoresCSV([]);
    };
    reader.readAsText(file);
  }, []);

  // Manejar cambio de fecha (single o range)
  const handleFechaChange = (selectedDates) => {
    if (modoFecha === "single") {
      if (selectedDates.length === 1) {
        setFechaSeleccionada(formatearFecha(selectedDates[0]));
      }
    } else {
      if (selectedDates.length === 2) {
        setFechaInicio(formatearFecha(selectedDates[0]));
        setFechaFin(formatearFecha(selectedDates[1]));
      }
    }
  };

  // Cargar solicitudes del sistema (todas las de Mercantil para primera comparacion)
  const cargarTodasSolicitudesMercantil = async () => {
    try {
      const params = {
        solo_nuevas: false, // Todas las solicitudes de Mercantil, sin importar estatus
      };

      // NOTA: No filtramos por fecha para encontrar órdenes aunque tengan fecha diferente
      // entre Mercantil (CSV) y nuestro sistema (puede haber diferencia por zona horaria)

      const response = await getSolicitudesMercantilNuevas(params);

      if (response.data?.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error al cargar solicitudes:", error);
      toast.error("Error al cargar solicitudes del sistema");
      return [];
    }
  };

  // Cargar solicitudes "Nueva Solicitud" del sistema (para segunda comparacion)
  const cargarSolicitudesNuevas = async () => {
    try {
      const params = {
        solo_nuevas: true, // Solo "Nueva Solicitud"
      };
      
      if (modoFecha === "single") {
        params.fecha = fechaSeleccionada;
      } else {
        params.fecha_inicio = fechaInicio;
        params.fecha_fin = fechaFin;
      }
      
      const response = await getSolicitudesMercantilNuevas(params);
      
      if (response.data?.data) {
        setSolicitudesSistema(response.data.data);
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error al cargar solicitudes:", error);
      toast.error("Error al cargar solicitudes del sistema");
      return [];
    }
  };

  // Ejecutar comparación
  const ejecutarComparacion = async () => {
    if (identificadoresCSV.length === 0) {
      toast.warning("Primero debe cargar un archivo CSV válido");
      return;
    }

    setCargando(true);
    setResultados(null);

    try {
      // Cargar TODAS las solicitudes de Mercantil (para verificar si identificadores existen)
      const todasSolicitudes = await cargarTodasSolicitudesMercantil();
      
      // Cargar solo solicitudes "Nueva Solicitud" de Mercantil
      const solicitudesNuevas = await cargarSolicitudesNuevas();
      
      if (todasSolicitudes.length === 0 && solicitudesNuevas.length === 0) {
        toast.info("No se encontraron solicitudes de Seguros Mercantil en el período seleccionado");
      }

      // Normalizar identificadores de TODAS las solicitudes (para primera comparacion)
      const idsTodasSolicitudes = todasSolicitudes.map(s => ({
        original: s.identificador,
        normalizado: s.identificador?.toString().toUpperCase().trim()
      }));

      const idsTodasSolicitudesSet = new Set(idsTodasSolicitudes.map(s => s.normalizado));
      const idsCSVSet = new Set(identificadoresCSV.map(d => d.identificador.toUpperCase().trim()));

      // PRIMERA COMPARACION: En CSV pero no en sistema (cualquier estatus)
      const enCSVSistemaNoEncontradas = identificadoresCSV.filter(
        d => !idsTodasSolicitudesSet.has(d.identificador.toUpperCase().trim())
      );

      // SEGUNDA COMPARACION: En sistema (solo "Nueva Solicitud") pero no en CSV
      const enSistemaNoEnCSV = solicitudesNuevas.filter(
        s => !idsCSVSet.has(s.identificador?.toString().toUpperCase().trim())
      );

      // Normalizar resultados para mantener compatibilidad con la tabla
      const enCSVSistemaNoEncontradasNormalizado = enCSVSistemaNoEncontradas.map(d => ({
        identificador: d.identificador,
        fechaCreacion: d.fechaCreacion
      }));

      // Estadísticas
      const stats = {
        totalCSV: identificadoresCSV.length,
        totalSistema: todasSolicitudes.length,
        totalNuevasSistema: solicitudesNuevas.length,
        enCSVNoSistema: enCSVSistemaNoEncontradas.length,
        enSistemaNoCSV: enSistemaNoEnCSV.length,
        coincidencias: identificadoresCSV.length - enCSVSistemaNoEncontradas.length,
      };

      setResultados({
        stats,
        enCSVSistemaNoEncontradas: enCSVSistemaNoEncontradasNormalizado,
        enSistemaNoEnCSV,
      });

      toast.success("Comparación completada");
    } catch (error) {
      console.error("Error en comparación:", error);
      toast.error("Error al ejecutar la comparación");
    } finally {
      setCargando(false);
    }
  };

  // Buscar y registrar una orden específica por identificador
  const handleBuscarYRegistrar = async () => {
    const identificador = identificadorBusqueda.trim().toUpperCase();
    if (!identificador) {
      toast.warning("Ingrese un identificador de orden");
      return;
    }

    setBuscandoIndividual(true);
    setResultadoIndividual(null);

    try {
      const res = await mercantilBuscarYRegistrar(identificador);
      const data = res?.data?.data;
      setResultadoIndividual(data);

      if (data?.estatus === "creada") {
        toast.success(`Orden ${identificador} registrada exitosamente en Farme.`);
      } else if (data?.estatus === "omitida") {
        toast.info(`Orden ${identificador} ya existe en Farme.`);
      } else {
        toast.error(
          `No se pudo registrar ${identificador}: ${data?.error_detalle || "Error desconocido"}`
        );
      }
    } catch (error) {
      console.error("Error en búsqueda individual:", error);
      const msgAmigable = mensajeErrorAmigable(error);
      toast.error(msgAmigable);
      setResultadoIndividual({ estatus: "fallida", error_detalle: msgAmigable });
    } finally {
      setBuscandoIndividual(false);
    }
  };

  // Exportar resultados a CSV
  const exportarResultados = () => {
    if (!resultados) return;

    let csvContent = "Tipo,Identificador,Titular,Farmacia,Fecha Creación\n";
    
    // Solicitudes en CSV no en sistema
    resultados.enCSVSistemaNoEncontradas.forEach(d => {
      csvContent += `En CSV no en sistema,${d.identificador},N/A,N/A,${d.fechaCreacion}\n`;
    });
    
    // Solicitudes en sistema no en CSV
    resultados.enSistemaNoEnCSV.forEach(s => {
      csvContent += `En sistema no en CSV,${s.identificador},"${s.titular || 'N/A'}",${s.farmacia_asignada || 'N/A'},${s.fecha_creacion || 'N/A'}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `comparacion_mercantil_${formatearFecha(new Date())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Resultados exportados");
  };

  const renderTabIndividual = () => (
    <Card className="shadow-none border">
              <CardHeader className="border-bottom-dashed">
                <h4 className="card-title mb-0">
                  <i className="ri-search-line me-2"></i>
                  Buscar y Registrar Orden Individual
                </h4>
              </CardHeader>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={5}>
                    <Label className="form-label">Identificador de Orden Mercantil</Label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej: A0000711904"
                      value={identificadorBusqueda}
                      onChange={(e) => setIdentificadorBusqueda(e.target.value)}
                      disabled={buscandoIndividual}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleBuscarYRegistrar();
                      }}
                    />
                    <small className="text-muted d-block mt-1">
                      Ingrese el número de orden exacto del portal Mercantil
                    </small>
                  </Col>
                  <Col md={3}>
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={handleBuscarYRegistrar}
                      disabled={buscandoIndividual || !identificadorBusqueda.trim()}
                    >
                      {buscandoIndividual ? (
                        <>
                          <LoaderButton />
                          Buscando...
                        </>
                      ) : (
                        <>
                          <i className="ri-search-line me-1"></i>
                          Buscar y Registrar
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>

                {resultadoIndividual && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <div
                        className={`alert ${
                          resultadoIndividual.estatus === "creada"
                            ? "alert-success"
                            : resultadoIndividual.estatus === "omitida"
                            ? "alert-info"
                            : "alert-danger"
                        } mb-0`}
                      >
                        <div className="d-flex align-items-center">
                          <i
                            className={`fs-20 me-2 ${
                              resultadoIndividual.estatus === "creada"
                                ? "ri-checkbox-circle-line"
                                : resultadoIndividual.estatus === "omitida"
                                ? "ri-information-line"
                                : "ri-close-circle-line"
                            }`}
                          ></i>
                          <div>
                            <strong>
                              {resultadoIndividual.estatus === "creada"
                                ? "Orden registrada"
                                : resultadoIndividual.estatus === "omitida"
                                ? "Orden omitida"
                                : "Error"}
                            </strong>
                            <p className="mb-0 fs-13">
                              {resultadoIndividual.estatus === "creada"
                                ? `La orden ${resultadoIndividual.identificador} fue creada exitosamente en Farme.`
                                : resultadoIndividual.estatus === "omitida"
                                ? `La orden ${resultadoIndividual.identificador} ya existe en el sistema.`
                                : resultadoIndividual.error_detalle || "Ocurrió un error inesperado."}
                            </p>
                            {resultadoIndividual.duracion_ms > 0 && (
                              <small className="text-muted">
                                Duración: {resultadoIndividual.duracion_ms}ms
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
  );

  const renderTabCSV = () => (
    <Card className="shadow-none border">
              <CardHeader className="border-bottom-dashed">
                <h4 className="card-title mb-0">
                  <i className="ri-file-upload-line me-2"></i>
                  Validar Solicitudes Mercantil contra CSV
                </h4>
              </CardHeader>
              <CardBody>
                {/* Filtros y Carga de Archivo */}
                <Row className="mb-4 align-items-end">
                  <Col md={4}>
                    <Label className="form-label">Archivo CSV de Mercantil</Label>
                    <div className="input-group">
                      <Input
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="form-control"
                      />
                    </div>
                    <small className="text-muted d-block mt-1">
                      {nombreArchivo ? `Cargado: ${nombreArchivo}` : "Seleccione el CSV descargado de Mercantil"}
                    </small>
                    {identificadoresCSV.length > 0 && (
                      <Badge color="success" className="mt-1">
                        {identificadoresCSV.length} órdenes detectadas
                      </Badge>
                    )}
                  </Col>
                  <Col md={5}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Label className="form-label mb-0">
                        {modoFecha === "single" ? "Fecha de Consulta" : "Rango de Fechas"}
                      </Label>
                      <div className="form-check form-switch">
                        <Input
                          type="switch"
                          id="modoFechaSwitch"
                          checked={modoFecha === "range"}
                          onChange={(e) => setModoFecha(e.target.checked ? "range" : "single")}
                        />
                        <Label for="modoFechaSwitch" className="form-check-label ms-1" style={{ fontSize: "0.75rem" }}>
                          {modoFecha === "single" ? "Una fecha" : "Rango"}
                        </Label>
                      </div>
                    </div>
                    <div className="position-relative">
                      <Flatpickr
                        key={modoFecha} // Force re-render when mode changes
                        className="form-control"
                        options={{
                          mode: modoFecha === "single" ? "single" : "range",
                          dateFormat: "d M, Y",
                          defaultDate: modoFecha === "single" 
                            ? new Date(fechaSeleccionada)
                            : [new Date(fechaInicio), new Date(fechaFin)],
                          locale: {
                            firstDayOfWeek: 1,
                            weekdays: {
                              shorthand: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
                              longhand: [
                                "Domingo", "Lunes", "Martes", "Miércoles",
                                "Jueves", "Viernes", "Sábado",
                              ],
                            },
                            months: {
                              shorthand: [
                                "Ene", "Feb", "Mar", "Abr", "May", "Jun",
                                "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
                              ],
                              longhand: [
                                "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                                "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
                              ],
                            },
                          },
                        }}
                        onChange={handleFechaChange}
                        placeholder={modoFecha === "single" ? "Seleccionar fecha" : "Seleccionar rango de fechas"}
                      />
                    </div>
                    <small className="text-muted d-block mt-1">
                      {modoFecha === "single" 
                        ? "Busca solicitudes del día seleccionado" 
                        : "Busca solicitudes en el rango de fechas"}
                    </small>
                  </Col>
                  <Col md={3}>
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={ejecutarComparacion}
                      disabled={cargando || identificadoresCSV.length === 0}
                    >
                      {cargando ? (
                        <>
                          <LoaderButton />
                          Comparando...
                        </>
                      ) : (
                        <>
                          <i className="ri-refresh-line me-1"></i>
                          Comparar
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>

                {/* Resumen de Estadísticas */}
                {resultados && (
                  <>
                    <Row className="mb-4">
                      <Col md={3}>
                        <Card className="card-animate border border-primary">
                          <CardBody className="text-center">
                            <h4 className="text-primary mb-0">{resultados.stats.totalCSV}</h4>
                            <small className="text-muted">Órdenes en CSV</small>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="card-animate border border-info">
                          <CardBody className="text-center">
                            <h4 className="text-info mb-0">{resultados.stats.totalSistema}</h4>
                            <small className="text-muted">Solicitudes en Sistema</small>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="card-animate border border-warning">
                          <CardBody className="text-center">
                            <h4 className="text-warning mb-0">{resultados.stats.enCSVNoSistema}</h4>
                            <small className="text-muted">En CSV, no en Sistema</small>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="card-animate border border-danger">
                          <CardBody className="text-center">
                            <h4 className="text-danger mb-0">{resultados.stats.enSistemaNoCSV}</h4>
                            <small className="text-muted">En Sistema, no en CSV</small>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>

                    {/* Botón Exportar */}
                    <Row className="mb-3">
                      <Col className="text-end">
                        <Button
                          color="success"
                          onClick={exportarResultados}
                          className="btn-sm"
                        >
                          <i className="ri-download-line me-1"></i>
                          Exportar Resultados
                        </Button>
                      </Col>
                    </Row>

                    {/* Tabla: En CSV no en Sistema */}
                    {resultados.enCSVSistemaNoEncontradas.length > 0 && (
                      <Row className="mb-4">
                        <Col>
                          <h5 className="text-warning mb-3">
                            <i className="ri-error-warning-line me-2"></i>
                            Órdenes en CSV no registradas en el sistema ({resultados.enCSVSistemaNoEncontradas.length})
                          </h5>
                          <div className="table-responsive">
                            <Table bordered hover size="sm">
                              <thead className="table-light">
                                <tr>
                                  <th>#</th>
                                  <th>Identificador Mercantil</th>
                                  <th>Fecha Creación (CSV)</th>
                                  <th>Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {resultados.enCSVSistemaNoEncontradas.map((d, index) => (
                                  <tr key={d.identificador}>
                                    <td>{index + 1}</td>
                                    <td><code>{d.identificador}</code></td>
                                    <td>{d.fechaCreacion}</td>
                                    <td>
                                      <Badge color="warning">No registrada</Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Tabla: En Sistema no en CSV */}
                    {resultados.enSistemaNoEnCSV.length > 0 && (
                      <Row className="mb-4">
                        <Col>
                          <h5 className="text-danger mb-3">
                            <i className="ri-error-warning-line me-2"></i>
                            Solicitudes en sistema no presentes en CSV de Mercantil ({resultados.enSistemaNoEnCSV.length})
                          </h5>
                          <div className="table-responsive">
                            <Table bordered hover size="sm">
                              <thead className="table-light">
                                <tr>
                                  <th>#</th>
                                  <th>Identificador</th>
                                  <th>Titular</th>
                                  <th>Farmacia Asignada</th>
                                  <th>Fecha Creación</th>
                                </tr>
                              </thead>
                              <tbody>
                                {resultados.enSistemaNoEnCSV.map((solicitud, index) => (
                                  <tr key={solicitud.identificador}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <code>{solicitud.identificador}</code>
                                    </td>
                                    <td>{solicitud.titular || "N/A"}</td>
                                    <td>{solicitud.farmacia_asignada || "N/A"}</td>
                                    <td>{solicitud.fecha_creacion || "N/A"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Mensaje si todo coincide */}
                    {resultados.enCSVSistemaNoEncontradas.length === 0 && 
                     resultados.enSistemaNoEnCSV.length === 0 && (
                      <Row>
                        <Col>
                          <div className="alert alert-success text-center">
                            <i className="ri-check-double-line display-4"></i>
                            <h5 className="mt-2">¡Todas las solicitudes coinciden!</h5>
                            <p className="mb-0">
                              Todas las órdenes del CSV están registradas en el sistema y
                              todas las solicitudes del sistema están presentes en el CSV.
                            </p>
                          </div>
                        </Col>
                      </Row>
                    )}
                  </>
                )}

                {/* Estado inicial */}
                {!resultados && !cargando && (
                  <div className="text-center py-5">
                    <i className="ri-file-upload-line display-4 text-muted"></i>
                    <h5 className="mt-3">Cargue un CSV para comenzar</h5>
                    <p className="text-muted">
                      Seleccione el archivo CSV descargado de Mercantil y haga clic en "Comparar"
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
  );

  const renderTabScraping = () => (
    <Card className="shadow-none border">
      <CardHeader className="border-bottom-dashed">
        <h4 className="card-title mb-0">
          <i className="ri-play-circle-line me-2"></i>
          Scraping masivo (portal Mercantil)
        </h4>
      </CardHeader>
      <CardBody>
        <p className="text-muted">
          Dispare una corrida manual del scraper para buscar y registrar órdenes nuevas
          en el portal de Seguros Mercantil. El scheduler ya ejecuta corridas automáticas
          cada 10 minutos en horario activo; este botón fuerza una corrida inmediata.
          También puede registrar una orden manualmente desde el wizard.
        </p>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-success"
            title="Buscar órdenes en Mercantil ahora"
            onClick={() => setShowScrapingModal(true)}
          >
            <i className="ri-search-2-line me-1"></i>
            Buscar órdenes en Mercantil ahora
          </button>
          <Link
            to="/solicitudes/crear-mercantil"
            className="btn btn-primary"
            title="Crear solicitud manual para Seguros Mercantil"
          >
            <i className="ri-add-line me-1"></i>
            Nueva solicitud Mercantil (manual)
          </Link>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => cambiarTab(TABS.historial)}
          >
            <i className="ri-history-line me-1"></i>
            Ver historial de corridas
          </button>
        </div>
        <hr />
        <div className="alert alert-info fs-13 mb-0">
          <i className="ri-information-line me-1"></i>
          Solo un Administrador puede usar estas acciones. Las corridas se registran
          automáticamente en el historial con detalle por orden.
        </div>
      </CardBody>
    </Card>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Validar Mercantil" pageTitle="Reportes" />

        <Card>
          <CardHeader className="pb-0">
            <Nav tabs className="nav-tabs-custom nav-success">
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({ active: activeTab === TABS.scraping })}
                  onClick={() => cambiarTab(TABS.scraping)}
                >
                  <i className="ri-play-circle-line me-1 align-bottom"></i>
                  Scraping masivo
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({ active: activeTab === TABS.individual })}
                  onClick={() => cambiarTab(TABS.individual)}
                >
                  <i className="ri-search-line me-1 align-bottom"></i>
                  Búsqueda individual
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({ active: activeTab === TABS.csv })}
                  onClick={() => cambiarTab(TABS.csv)}
                >
                  <i className="ri-file-upload-line me-1 align-bottom"></i>
                  Validar CSV
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({ active: activeTab === TABS.historial })}
                  onClick={() => cambiarTab(TABS.historial)}
                >
                  <i className="ri-history-line me-1 align-bottom"></i>
                  Historial de corridas
                </NavLink>
              </NavItem>
            </Nav>
          </CardHeader>
          <CardBody>
            <TabContent activeTab={activeTab}>
              <TabPane tabId={TABS.scraping}>{renderTabScraping()}</TabPane>
              <TabPane tabId={TABS.individual}>{renderTabIndividual()}</TabPane>
              <TabPane tabId={TABS.csv}>{renderTabCSV()}</TabPane>
              <TabPane tabId={TABS.historial}>
                {activeTab === TABS.historial && <HistorialCorridas />}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>

        <MercantilScrapingModal
          isOpen={showScrapingModal}
          toggle={() => setShowScrapingModal(false)}
        />
      </Container>
    </div>
  );
};

export default ValidarMercantil;
